/**
 * An array of question objects used to assess a user's investment risk profile.
 *
 * Each question object contains:
 * - `text`: The question presented to the user.
 * - `options`: An array of possible answers, each with:
 *    - `label`: The text label for the answer option.
 *    - `score`: The numerical score associated with the answer, used for risk profiling.
 *
 * The scoring system is designed so that higher scores generally indicate a greater willingness to take investment risks.
 *
 * @type {Array<{
 *   text: string,
 *   options: Array<{
 *     label: string,
 *     score: number
 *   }>
 * }>}
 */
export const questions = [
  {
    text: "People who know me would describe me as a cautious person.",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
  {
    text: "I feel comfortable about investing in the stock market.",
    options: [
      { label: "Strongly Agree", score: 4 },
      { label: "Agree", score: 3 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 1 },
      { label: "Strongly Disagree", score: 0 },
    ],
  },
  {
    text: "I generally look for safer investments, even if that means lower returns.",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
  {
    text: "Usually it takes me a long time to make up my mind on financial matters.",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
  {
    text: "I associate the word risk with the idea of opportunity.",
    options: [
      { label: "Strongly Agree", score: 4 },
      { label: "Agree", score: 3 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 1 },
      { label: "Strongly Disagree", score: 0 },
    ],
  },
  {
    text: "I generally prefer bank deposits to riskier investments.",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
  {
    text: "I find investment matters easy to understand.",
    options: [
      { label: "Strongly Agree", score: 4 },
      { label: "Agree", score: 3 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 1 },
      { label: "Strongly Disagree", score: 0 },
    ],
  },
  {
    text: "I'm willing to take substantial investment risk to earn substantial returns.",
    options: [
      { label: "Strongly Agree", score: 4 },
      { label: "Agree", score: 3 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 1 },
      { label: "Strongly Disagree", score: 0 },
    ],
  },
  {
    text: "I have little experience of investing in stocks and shares. ",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
  {
    text: "I tend to be anxious about the investment decisions I've made.",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
  {
    text: "I'd rather take my chances with higher risk investments than increase the amount I'm saving.",
    options: [
      { label: "Strongly Agree", score: 4 },
      { label: "Agree", score: 3 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 1 },
      { label: "Strongly Disagree", score: 0 },
    ],
  },
  {
    text: "I'm concerned by the volatility of stockmarket investments. ",
    options: [
      { label: "Strongly Agree", score: 0 },
      { label: "Agree", score: 1 },
      { label: "No Strong Opinion", score: 2 },
      { label: "Disagree", score: 3 },
      { label: "Strongly Disagree", score: 4 },
    ],
  },
];

/**
 * An array of risk category objects used to classify risk profiles based on score ranges.
 *
 * Each object contains:
 * - category: {string} The name of the risk category.
 * - low: {number} The inclusive lower bound of the score range for this category.
 * - high: {number} The inclusive upper bound of the score range for this category.
 *
 * Example usage:
 *   // Find the category for a given score
 *   const score = 18;
 *   const category = riskCategories.find(
 *     ({ low, high }) => score >= low && score <= high
 *   );
 *   console.log(category.category); // "Balanced"
 */
export const riskCategories = [
  { category: "Risk Averse", low: 0, high: 10 },
  { category: "Conservative", low: 11, high: 15 },
  { category: "Balanced", low: 16, high: 22 },
  { category: "Moderate", low: 23, high: 28 },
  { category: "Dynamic", low: 29, high: 34 },
  { category: "Adventurous", low: 35, high: 48 },
];
